import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FiRefreshCw, FiArrowLeft, FiXCircle, FiSearch, FiEdit2, FiRotateCcw, FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  getEntries, getEntry, cancelEntry, patchEntry, resubmitEntry,
  getCostCenters, validateCostCenter,
  getProfitCenters, validateProfitCenter,
  getWbsElements,
} from '../../utils/journalApi';
import StatusBadge from './StatusBadge';

const genId = () => Math.random().toString(36).slice(2);

const STATUSES   = ['', 'PENDING', 'VALIDATING', 'AWAITING_APPROVAL', 'POSTING', 'POSTED', 'FAILED', 'CANCELLED', 'DRAFT'];
const PAGE_SIZE  = 20;
const CANCELLABLE = ['PENDING', 'VALIDATING', 'AWAITING_APPROVAL', 'DRAFT'];

/* ══════════════════════════════════════════════
   ENTRY DETAIL SUB-VIEW
══════════════════════════════════════════════ */
function EntryDetail({ trackingId, onBack }) {
  const [entry, setEntry]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [cancelling, setCancelling]   = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [editing, setEditing]         = useState(false);
  const [editHeader, setEditHeader]   = useState({});
  const [editLines, setEditLines]     = useState([]);
  const [editErr, setEditErr]         = useState({});
  const [saving, setSaving]           = useState(false);

  // Master data for edit form
  const [masterLoading, setMasterLoading] = useState(false);
  const [costCenters, setCostCenters]     = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [wbsElements, setWbsElements]     = useState([]);
  const [wbsDisabled, setWbsDisabled]     = useState(false);
  const [lineVal, setLineVal]             = useState({});
  const validatingRef = useRef({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await getEntry(trackingId); setEntry(r.data); } catch (_) {}
      setLoading(false);
    })();
  }, [trackingId]);

  const loadMasterData = async (cc) => {
    setMasterLoading(true);
    try {
      const [ccRes, pcRes, wbsRes] = await Promise.all([
        getCostCenters(cc),
        getProfitCenters(cc),
        getWbsElements(cc),
      ]);
      setCostCenters(ccRes.data || []);
      setProfitCenters(pcRes.data || []);
      const wbs = wbsRes.data || [];
      setWbsElements(wbs);
      setWbsDisabled(wbs.length === 0);
    } catch (_) {}
    setMasterLoading(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this journal entry?')) return;
    setCancelling(true);
    try {
      await cancelEntry(trackingId);
      toast.success('Entry cancelled.');
      setEntry(e => ({ ...e, status: 'CANCELLED' }));
    } catch (_) {}
    setCancelling(false);
  };

  const handleResubmit = async () => {
    if (!window.confirm('Resubmit this draft entry through the full pipeline?')) return;
    setResubmitting(true);
    try {
      const r = await resubmitEntry(trackingId);
      toast.success('Entry resubmitted.');
      setEntry(r.data);
    } catch (_) {}
    setResubmitting(false);
  };

  const openEdit = async () => {
    setEditing(true);
    setEditErr({});
    setLineVal({});
    let e = entry;
    try { const r = await getEntry(trackingId); e = r.data; setEntry(e); } catch (_) {}

    setEditHeader({
      document_date: e.document_date  || '',
      posting_date:  e.posting_date   || '',
      document_type: e.document_type  || '',
      currency:      e.currency       || '',
      reference:     e.reference      || '',
      header_text:   e.header_text    || '',
    });

    const defaultLines = [
      { _id: genId(), posting_key: '40', gl_account: '', amount: '', cost_center: '', profit_center: '', wbs_element: '', item_text: '' },
      { _id: genId(), posting_key: '50', gl_account: '', amount: '', cost_center: '', profit_center: '', wbs_element: '', item_text: '' },
    ];
    setEditLines(
      Array.isArray(e.line_items) && e.line_items.length > 0
        ? e.line_items.map(l => ({ ...l, _id: genId() }))
        : defaultLines
    );

    if (e.company_code) loadMasterData(e.company_code);
  };

  /* ── Edit line helpers ── */
  const setEL = (id, k, v) => {
    setEditLines(ls => ls.map(l => l._id === id ? { ...l, [k]: v } : l));
    setEditErr(e => ({ ...e, [`${id}.${k}`]: '' }));
  };

  const addEditLine = () => setEditLines(ls => [...ls, {
    _id: genId(), posting_key: '', gl_account: '', amount: '',
    cost_center: '', profit_center: '', wbs_element: '', item_text: '',
  }]);

  const removeEditLine = (id) => {
    if (editLines.length <= 2) { toast.warn('Minimum 2 line items required.'); return; }
    setEditLines(ls => ls.filter(l => l._id !== id));
    setLineVal(v => {
      const next = { ...v };
      delete next[`${id}.cost_center`];
      delete next[`${id}.profit_center`];
      return next;
    });
  };

  /* ── Edit master field handlers ── */
  const handleCCChange = (lineId, value) => {
    setEL(lineId, 'cost_center', value);
    const match = costCenters.find(c => c.cost_center === value);
    if (match) {
      if (match.profit_center) setEL(lineId, 'profit_center', match.profit_center);
      setLineVal(v => ({ ...v, [`${lineId}.cost_center`]: { state: 'ok', msg: '' } }));
    } else {
      setLineVal(v => ({ ...v, [`${lineId}.cost_center`]: null }));
    }
  };

  const handleCCBlur = async (lineId, value, companyCode) => {
    if (!value || !companyCode) return;
    const key = `${lineId}.cost_center`;
    if (validatingRef.current[key]) return;
    const match = costCenters.find(c => c.cost_center === value);
    if (match) { setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } })); return; }
    validatingRef.current[key] = true;
    try {
      const res = await validateCostCenter(value, companyCode);
      setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } }));
      if (res.data?.profit_center) setEL(lineId, 'profit_center', res.data.profit_center);
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 404 ? 'Cost centre not found' : status === 422 ? 'Cost centre is blocked or expired' : '';
      setLineVal(v => ({ ...v, [key]: { state: 'error', msg } }));
    } finally { validatingRef.current[key] = false; }
  };

  const handlePCChange = (lineId, value) => {
    setEL(lineId, 'profit_center', value);
    const match = profitCenters.find(p => p.profit_center === value);
    if (match) setLineVal(v => ({ ...v, [`${lineId}.profit_center`]: { state: 'ok', msg: '' } }));
    else setLineVal(v => ({ ...v, [`${lineId}.profit_center`]: null }));
  };

  const handlePCBlur = async (lineId, value, companyCode) => {
    if (!value || !companyCode) return;
    const key = `${lineId}.profit_center`;
    if (validatingRef.current[key]) return;
    const match = profitCenters.find(p => p.profit_center === value);
    if (match) { setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } })); return; }
    validatingRef.current[key] = true;
    try {
      await validateProfitCenter(value, companyCode);
      setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } }));
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 404 ? 'Profit centre not found' : status === 422 ? 'Profit centre is blocked or expired' : '';
      setLineVal(v => ({ ...v, [key]: { state: 'error', msg } }));
    } finally { validatingRef.current[key] = false; }
  };

  const ccBorder = (id) => lineVal[`${id}.cost_center`]?.state === 'ok' ? '#22c55e' : lineVal[`${id}.cost_center`]?.state === 'error' ? '#ef4444' : undefined;
  const pcBorder = (id) => lineVal[`${id}.profit_center`]?.state === 'ok' ? '#22c55e' : lineVal[`${id}.profit_center`]?.state === 'error' ? '#ef4444' : undefined;

  /* ── Save edit ── */
  const handleSaveEdit = async () => {
    const errs = {};
    editLines.forEach(l => {
      if (!['40', '50'].includes(l.posting_key)) errs[`${l._id}.posting_key`] = 'Required';
      if (!l.gl_account) errs[`${l._id}.gl_account`] = 'Required';
      if (!l.amount || Number(l.amount) <= 0) errs[`${l._id}.amount`] = '> 0';
    });
    if (Object.keys(errs).length) { setEditErr(errs); toast.error('Fix errors before saving.'); return; }

    const debits  = editLines.filter(l => l.posting_key === '40').reduce((s, l) => s + Number(l.amount || 0), 0);
    const credits = editLines.filter(l => l.posting_key === '50').reduce((s, l) => s + Number(l.amount || 0), 0);
    if (Math.abs(debits - credits) >= 0.001) { toast.error('Debits must equal credits.'); return; }

    if (Object.values(lineVal).some(v => v?.state === 'error')) {
      toast.error('Fix cost/profit centre validation errors.');
      return;
    }

    setSaving(true);
    try {
      const r = await patchEntry(trackingId, {
        ...editHeader,
        line_items: editLines.map(({ _id, ...l }, idx) => ({
          line_item_id:  l.line_item_id ?? idx + 1,
          posting_key:   l.posting_key,
          gl_account:    l.gl_account,
          amount:        String(Number(l.amount).toFixed(2)),
          cost_center:   l.cost_center   || undefined,
          profit_center: l.profit_center || undefined,
          wbs_element:   l.wbs_element   || undefined,
          item_text:     l.item_text     || undefined,
        })),
      });
      toast.success('Draft updated.');
      setEntry(r.data);
      setEditing(false);
    } catch (_) {}
    setSaving(false);
  };

  if (loading) return <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>;
  if (!entry)  return <div className="journal-empty">Entry not found.</div>;

  const isDraft    = entry.status === 'DRAFT';
  const canCancel  = CANCELLABLE.includes(entry.status);
  const validation = entry.validation_results || {};
  const agentLog   = entry.agent_log || [];
  const loadingDot = masterLoading
    ? <span style={{ marginLeft: 4, display: 'inline-block', verticalAlign: 'middle' }}><div className="journal-spinner-sm" style={{ display: 'inline-block' }} /></span>
    : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <button className="j-back-link" onClick={onBack}><FiArrowLeft size={13} /> Back to list</button>
        <div style={{ display: 'flex', gap: 8 }}>
          {isDraft && !editing && (
            <>
              <button className="journal-btn ghost small" onClick={openEdit}>
                <FiEdit2 size={13} /> Edit Draft
              </button>
              <button className="journal-btn primary small" onClick={handleResubmit} disabled={resubmitting}>
                <FiRotateCcw size={13} /> {resubmitting ? 'Resubmitting…' : 'Resubmit'}
              </button>
            </>
          )}
          {canCancel && (
            <button className="journal-btn danger small" onClick={handleCancel} disabled={cancelling}>
              <FiXCircle size={13} /> {cancelling ? 'Cancelling…' : 'Cancel Entry'}
            </button>
          )}
          {isDraft && !editing && (
            <button className="journal-btn danger small" onClick={handleCancel} disabled={cancelling}>
              <FiXCircle size={13} /> {cancelling ? 'Cancelling…' : 'Cancel Entry'}
            </button>
          )}
        </div>
      </div>

      {/* ── Inline DRAFT editor ── */}
      {editing && (() => {
        const debits   = editLines.filter(l => l.posting_key === '40').reduce((s, l) => s + Number(l.amount || 0), 0);
        const credits  = editLines.filter(l => l.posting_key === '50').reduce((s, l) => s + Number(l.amount || 0), 0);
        const balanced = debits > 0 && credits > 0 && Math.abs(debits - credits) < 0.001;

        const hf = (key, label, type = 'text', extra = {}) => (
          <div className="journal-field" key={key}>
            <label>{label}</label>
            <input
              type={type}
              value={editHeader[key] || ''}
              onChange={e => { setEditHeader(h => ({ ...h, [key]: e.target.value })); setEditErr(er => ({ ...er, [key]: '' })); }}
              className={editErr[key] ? 'err' : ''}
              {...extra}
            />
            {editErr[key] && <span className="journal-field-err">{editErr[key]}</span>}
          </div>
        );

        return (
          <div className="journal-panel" style={{ marginBottom: 14 }}>
            <div className="journal-panel-head">
              <h3>Edit Draft Entry</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="journal-btn ghost small" onClick={() => setEditing(false)}>Discard</button>
                <button
                  className="journal-btn primary small"
                  onClick={handleSaveEdit}
                  disabled={saving || !balanced}
                >
                  <FiCheck size={13} /> {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Header fields */}
            <div className="journal-field-grid" style={{ marginBottom: 14 }}>
              {hf('document_date', 'Document Date', 'date')}
              {hf('posting_date',  'Posting Date',  'date')}
              {hf('document_type', 'Doc Type',  'text', { maxLength: 2, placeholder: 'SA / KR' })}
              {hf('currency',      'Currency',  'text', { maxLength: 3, placeholder: 'INR' })}
              {hf('reference',     'Reference', 'text', { maxLength: 16 })}
              {hf('header_text',   'Header Text', 'text', { maxLength: 25 })}
            </div>

            {/* Line items */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--j-txt)' }}>Line Items</span>
              <button className="journal-btn ghost small" onClick={addEditLine}><FiPlus size={12} /> Add Line</button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="line-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>#</th>
                    <th style={{ width: 90 }}>PK *<br /><small style={{ fontWeight: 400 }}>40=Dr 50=Cr</small></th>
                    <th style={{ width: 110 }}>GL Account *</th>
                    <th style={{ width: 105 }}>Amount *</th>
                    <th style={{ minWidth: 110 }}>Cost CTR{loadingDot}</th>
                    <th style={{ minWidth: 110 }}>Profit CTR{loadingDot}</th>
                    <th style={{ minWidth: 100 }}>WBS{loadingDot}</th>
                    <th>Item Text</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editLines.map((l, idx) => {
                    const ccErr = lineVal[`${l._id}.cost_center`]?.state === 'error' ? lineVal[`${l._id}.cost_center`].msg : '';
                    const pcErr = lineVal[`${l._id}.profit_center`]?.state === 'error' ? lineVal[`${l._id}.profit_center`].msg : '';
                    return (
                      <tr key={l._id}>
                        <td style={{ color: '#a0a7c0', fontSize: 11, textAlign: 'center' }}>{idx + 1}</td>
                        <td>
                          <select
                            value={l.posting_key}
                            onChange={e => setEL(l._id, 'posting_key', e.target.value)}
                            className={editErr[`${l._id}.posting_key`] ? 'err' : ''}
                          >
                            <option value="">—</option>
                            <option value="40">40 (Dr)</option>
                            <option value="50">50 (Cr)</option>
                          </select>
                        </td>
                        <td>
                          <input value={l.gl_account || ''} maxLength={10}
                            onChange={e => setEL(l._id, 'gl_account', e.target.value)}
                            className={editErr[`${l._id}.gl_account`] ? 'err' : ''} />
                        </td>
                        <td>
                          <input type="number" min="0.01" step="0.01" value={l.amount || ''}
                            onChange={e => setEL(l._id, 'amount', e.target.value)}
                            className={editErr[`${l._id}.amount`] ? 'err' : ''} />
                        </td>

                        {/* Cost CTR */}
                        <td>
                          <input
                            list={`edit-cc-${l._id}`}
                            value={l.cost_center || ''}
                            maxLength={10}
                            placeholder={masterLoading ? 'Loading…' : ''}
                            onChange={e => handleCCChange(l._id, e.target.value)}
                            onBlur={() => handleCCBlur(l._id, l.cost_center, entry?.company_code)}
                            style={{ borderColor: ccBorder(l._id) }}
                          />
                          <datalist id={`edit-cc-${l._id}`}>
                            {costCenters.map(c => <option key={c.cost_center} value={c.cost_center} />)}
                          </datalist>
                          {ccErr && <div style={{ fontSize: '0.68rem', color: '#ef4444', lineHeight: 1.2, marginTop: 2 }}>{ccErr}</div>}
                        </td>

                        {/* Profit CTR */}
                        <td>
                          <input
                            list={`edit-pc-${l._id}`}
                            value={l.profit_center || ''}
                            maxLength={10}
                            placeholder={masterLoading ? 'Loading…' : ''}
                            onChange={e => handlePCChange(l._id, e.target.value)}
                            onBlur={() => handlePCBlur(l._id, l.profit_center, entry?.company_code)}
                            style={{ borderColor: pcBorder(l._id) }}
                          />
                          <datalist id={`edit-pc-${l._id}`}>
                            {profitCenters.map(p => <option key={p.profit_center} value={p.profit_center} />)}
                          </datalist>
                          {pcErr && <div style={{ fontSize: '0.68rem', color: '#ef4444', lineHeight: 1.2, marginTop: 2 }}>{pcErr}</div>}
                        </td>

                        {/* WBS */}
                        <td>
                          <input
                            list={`edit-wbs-${l._id}`}
                            value={l.wbs_element || ''}
                            maxLength={24}
                            disabled={wbsDisabled}
                            placeholder={wbsDisabled ? 'N/A' : masterLoading ? 'Loading…' : ''}
                            onChange={e => setEL(l._id, 'wbs_element', e.target.value)}
                            style={{ background: wbsDisabled ? '#f5f7fa' : undefined, color: wbsDisabled ? '#a0a7c0' : undefined }}
                          />
                          {!wbsDisabled && (
                            <datalist id={`edit-wbs-${l._id}`}>
                              {wbsElements.map(w => <option key={w.wbs_element} value={w.wbs_element}>{w.description}</option>)}
                            </datalist>
                          )}
                        </td>

                        <td>
                          <input value={l.item_text || ''} maxLength={50}
                            onChange={e => setEL(l._id, 'item_text', e.target.value)} />
                        </td>
                        <td>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, display: 'flex' }}
                            onClick={() => removeEditLine(l._id)}
                          ><FiTrash2 size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="balance-bar" style={{ marginTop: 10 }}>
              <div className="b-item">Debits (40)<strong>{debits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
              <div className="b-item">Credits (50)<strong>{credits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
              <div className="b-item">Diff<strong>{Math.abs(debits - credits).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
              <div className={`b-status ${balanced ? 'ok' : 'bad'}`}>{balanced ? '✓ Balanced' : '✗ Unbalanced'}</div>
            </div>
          </div>
        );
      })()}

      {/* ── Header card ── */}
      <div className="journal-panel">
        <div className="journal-panel-head">
          <h3>Entry — <code style={{ fontFamily: 'monospace', fontSize: 13 }}>{entry.tracking_id}</code></h3>
          <StatusBadge status={entry.status} />
        </div>
        <div className="j-detail-grid">
          {[
            ['Company Code',  entry.company_code],
            ['Document Date', entry.document_date],
            ['Posting Date',  entry.posting_date],
            ['Doc Type',      entry.document_type],
            ['Currency',      entry.currency],
            ['Reference',     entry.reference || '—'],
            ['Header Text',   entry.header_text || '—'],
            ['Submitted By',  entry.submitted_by],
            ['Submitted At',  entry.submitted_at ? new Date(entry.submitted_at).toLocaleString() : '—'],
            ...(entry.sap_doc_number ? [['SAP Doc #', entry.sap_doc_number], ['Fiscal Year', entry.fiscal_year || '—']] : []),
          ].map(([label, val]) => (
            <div
              className={`j-detail-item${label === 'Submitted By' || label === 'Submitted At' ? ' span2' : ''}`}
              key={label}
            >
              <label>{label}</label>
              <div className="val" style={label === 'SAP Doc #' ? { color: '#15803d', fontWeight: 700 } : {}}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Line Items (read-only) ── */}
      {entry.line_items?.length > 0 && (
        <div className="journal-panel">
          <div className="journal-panel-head"><h3>Line Items</h3></div>
          <div className="journal-table-wrap">
            <table className="journal-table">
              <thead>
                <tr>
                  <th>#</th><th>PK</th><th>GL Account</th><th>Amount</th>
                  <th>Cost CTR</th><th>Profit CTR</th><th>WBS</th><th>Item Text</th>
                </tr>
              </thead>
              <tbody>
                {entry.line_items.map((l, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <span className={`j-chip ${l.posting_key === '40' ? 'VALIDATING' : 'AWAITING_APPROVAL'}`}>
                        {l.posting_key === '40' ? 'Dr 40' : 'Cr 50'}
                      </span>
                    </td>
                    <td><strong>{l.gl_account}</strong></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="journal-muted">{l.cost_center  || '—'}</td>
                    <td className="journal-muted">{l.profit_center || '—'}</td>
                    <td className="journal-muted">{l.wbs_element  || '—'}</td>
                    <td className="journal-muted">{l.item_text    || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Validation Results ── */}
      <div className="journal-panel">
        <div className="journal-panel-head"><h3>Validation Results</h3></div>
        {validation.passed ? (
          <div className="j-val-banner ok">All validations passed</div>
        ) : ((validation.errors?.length || validation.warnings?.length)
          ? <div className="j-val-banner err">Validation issues found</div>
          : null
        )}
        {[
          ...(validation.errors   || []).map(e => ({ ...e, _type: 'ERROR' })),
          ...(validation.warnings || []).map(w => ({ ...w, _type: 'WARNING' })),
        ].map((v, i) => (
          <ul key={i} className="j-val-list">
            <li className="j-val-item">
              <StatusBadge status={v._type} />
              <div>
                {(v.field || v.code || v.error_code) && (
                  <div className="j-val-field">
                    {v.field && <span>{v.field} </span>}
                    <span className="j-val-code">{v.code || v.error_code}</span>
                  </div>
                )}
                <div className="j-val-msg">{v.message}</div>
              </div>
            </li>
          </ul>
        ))}
        {!validation.passed && !validation.errors?.length && !validation.warnings?.length && (
          <div className="journal-empty">No validation data.</div>
        )}
      </div>

      {/* ── Agent Log ── */}
      {agentLog.length > 0 && (
        <div className="journal-panel">
          <div className="journal-panel-head"><h3>Agent Processing Log</h3></div>
          <div className="j-timeline">
            {agentLog.map((step, i) => {
              const isOk  = step.result === 'SUCCESS' || step.result === 'PASSED';
              const isErr = step.result === 'FAILED'  || step.result === 'ERROR';
              return (
                <div className="j-step" key={i}>
                  <div className={`j-dot ${isOk ? 'ok' : isErr ? 'err' : ''}`} />
                  <div className="j-step-body">
                    <div className="j-step-head">
                      <span className="j-step-agent">{step.agent_name || step.agent}</span>
                      <span className="j-step-dur">{step.duration_ms != null ? `${step.duration_ms}ms` : ''}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="j-step-action">{step.action}</span>
                      {step.result && <StatusBadge status={step.result} />}
                    </div>
                    {step.detail && <div className="j-step-detail">{step.detail}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   ENTRY LIST VIEW
══════════════════════════════════════════════ */
export default function JournalEntryList({ onNavigate }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ company_code: '', status: '', submitted_by: '' });

  const load = useCallback(async (pg = 1, f = filters) => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE_SIZE };
      if (f.company_code) params.company_code = f.company_code;
      if (f.status)       params.status       = f.status;
      if (f.submitted_by) params.submitted_by = f.submitted_by;
      const res = await getEntries(params);
      setEntries(res.data.items ?? res.data);
      setTotal(res.data.total ?? (res.data.items ?? res.data).length);
    } catch (_) {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (selected) return <EntryDetail trackingId={selected} onBack={() => setSelected(null)} />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Filters */}
      <div className="journal-filters">
        <label>Company Code
          <input className="journal-input" placeholder="e.g. 1000" maxLength={4} value={filters.company_code}
            onChange={e => setFilters(f => ({ ...f, company_code: e.target.value }))} style={{ width: 120 }} />
        </label>
        <label>Status
          <select className="journal-input" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            {STATUSES.map(s => <option key={s} value={s}>{s || '— All —'}</option>)}
          </select>
        </label>
        <label>Submitted By
          <input className="journal-input" placeholder="Name or email" value={filters.submitted_by}
            onChange={e => setFilters(f => ({ ...f, submitted_by: e.target.value }))} style={{ width: 150 }} />
        </label>
        <button className="journal-btn primary small" onClick={() => { setPage(1); load(1); }}>
          <FiSearch size={13} /> Search
        </button>
        <button className="journal-btn ghost small" onClick={() => {
          const f = { company_code: '', status: '', submitted_by: '' };
          setFilters(f); setPage(1); load(1, f);
        }}>
          <FiRefreshCw size={13} /> Reset
        </button>
      </div>

      <div className="journal-panel">
        <div className="journal-panel-head">
          <h3>Journal Entries</h3>
          <span className="journal-muted" style={{ fontSize: '0.82rem' }}>{total} total</span>
        </div>

        {loading ? (
          <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
        ) : entries.length === 0 ? (
          <div className="journal-empty">No entries found.</div>
        ) : (
          <>
            <div className="journal-table-wrap">
              <table className="journal-table">
                <thead>
                  <tr>
                    <th>Tracking ID</th><th>Status</th><th>Company</th>
                    <th>Posting Date</th><th>Doc Type</th><th>Currency</th>
                    <th>SAP Doc #</th><th>Submitted By</th><th>Submitted At</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.tracking_id} className="clickable" onClick={() => setSelected(e.tracking_id)}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.tracking_id}</td>
                      <td><StatusBadge status={e.status} /></td>
                      <td>{e.company_code}</td>
                      <td>{e.posting_date}</td>
                      <td>{e.document_type}</td>
                      <td>{e.currency}</td>
                      <td>{e.sap_doc_number || '—'}</td>
                      <td>{e.submitted_by}</td>
                      <td style={{ fontSize: 11 }}>{e.submitted_at ? new Date(e.submitted_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="j-pager">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              {totalPages > 5 && <span className="journal-muted" style={{ padding: '0 4px' }}>…{totalPages}</span>}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
